import axios from "axios";
import moment from "moment";

// Vitals the patient records themselves (weight, blood pressure ...).
//
// WHERE THEY LIVE. The portal API has no endpoint for patient-entered
// observations, so:
//   - weight and height are ALSO written to the questionnaire
//     (api/Profile/UpdateQuestionaire), the one place the clinic already reads
//     them from, so the clinic sees the latest value and BMI updates;
//   - the dated history behind the graphs is kept in this browser
//     (localStorage, per patient). It is not shared with the clinic and does
//     not follow the patient to another device. When the API gains an
//     endpoint, replace readEntries / addEntries / removeEntry below; nothing
//     else needs to change.

export const VITAL_TYPES = [
  { key: "weight", label: "Weight", unit: "kg", min: 20, max: 400, step: "0.1" },
  { key: "height", label: "Height", unit: "m", min: 0.5, max: 2.6, step: "0.01" },
  { key: "bpSystolic", label: "Blood pressure (systolic)", unit: "mmHg", min: 50, max: 260, step: "1", group: "bp" },
  { key: "bpDiastolic", label: "Blood pressure (diastolic)", unit: "mmHg", min: 30, max: 160, step: "1", group: "bp" },
  { key: "oxygen", label: "Oxygen saturation", unit: "%", min: 50, max: 100, step: "1" },
  { key: "heartRate", label: "Heart rate", unit: "bpm", min: 20, max: 250, step: "1" },
  { key: "temperature", label: "Temperature", unit: "°C", min: 30, max: 45, step: "0.1" },
];

const typeOf = (key) => VITAL_TYPES.find((t) => t.key === key);
const storeKey = () => `ppVitals:${localStorage.getItem("UserID")}`;

export function readEntries() {
  try {
    const all = JSON.parse(localStorage.getItem(storeKey()) || "[]");
    return Array.isArray(all) ? all.filter((e) => typeOf(e.type)) : [];
  } catch {
    return [];
  }
}

function writeEntries(list) {
  try {
    localStorage.setItem(storeKey(), JSON.stringify(list));
  } catch {
    throw new Error("This browser is not allowing the portal to save your entries.");
  }
}

export function addEntries(newOnes) {
  const stamp = Date.now();
  const list = [
    ...readEntries(),
    ...newOnes.map((e, i) => ({ id: `${stamp}-${i}`, ...e })),
  ];
  writeEntries(list);
  return list;
}

export function removeEntry(id) {
  const list = readEntries().filter((e) => e.id !== id);
  writeEntries(list);
  return list;
}

// Entries as result lines, the shape the results graphs and table read.
export function entriesAsResults(entries) {
  return entries.map((e) => {
    const t = typeOf(e.type);
    return {
      text: t.label,
      identifier: "PATIENT",
      observationValue: String(e.value),
      units: t.unit,
      referenceRange: "",
      abnormalFlags: "",
      dateTimeofTransaction: e.date,
      enteredByPatient: true,
    };
  });
}

// The questionnaire carries the current weight and height; keep it in step.
export async function syncQuestionnaire({ weight, height }) {
  const url = process.env.REACT_APP_OXAR_API_URL;
  const id = localStorage.getItem("UserID");
  const res = await axios(`${url}api/Profile/GetQuestionairebyPatientId?id=${id}`);
  const q = res.data.content || {};
  ["whatDateWasYourLastMenstrualPeriod", "whenWasYourLastSmearTest"].forEach((k) => {
    const d = q[k] ? String(q[k]).substring(0, 10) : "";
    q[k] = d === "0001-01-01" ? "" : d;
  });
  if (weight != null) q.weightinKilograms = weight;
  if (height != null) q.heightinMetres = height;
  await axios.put(`${url}/api/Profile/UpdateQuestionaire?userId=${id}`, q);
  return q;
}

export async function readClinicBody() {
  const url = process.env.REACT_APP_OXAR_API_URL;
  const id = localStorage.getItem("UserID");
  const res = await axios(`${url}api/Profile/GetQuestionairebyPatientId?id=${id}`);
  const q = res.data.content || {};
  return { weight: q.weightinKilograms, height: q.heightinMetres };
}

export const today = () => moment().format("YYYY-MM-DDTHH:mm");
